import { useMutation, useQuery } from '@tanstack/react-query';
import { EmptyState, Icon, ListGroup, ListRow, Skeleton } from '@uplink/ui';
import { errorText, useTRPC } from '../api.ts';
import { MainActionBar } from '../native-controls.tsx';
import { usePrefs } from '../prefs.tsx';
import { closeApp, haptic, inTelegram } from '../telegram.ts';
import { BROADCAST_STATUS, StatusBadge } from './common.tsx';

const AUDIENCE: Record<string, string> = { all: 'всем', ru: 'русскоязычным', en: 'англоязычным' };

/**
 * Broadcast history. A new broadcast is composed in the bot chat, where premium emoji, stickers and
 * formatting are at hand: the button starts a draft there and closes the app into that chat.
 */
export function AdminBroadcastsScreen() {
  const trpc = useTRPC();
  const { dateTime } = usePrefs();
  const list = useQuery({
    ...trpc.admin.broadcasts.queryOptions(),
    // Follow a broadcast on its way
    refetchInterval: (q) => (q.state.data?.rows.some((b) => b.status === 'sending') ? 3_000 : false),
  });
  const start = useMutation(
    trpc.admin.newBroadcast.mutationOptions({
      onSuccess: () => {
        haptic.success();
        closeApp();
      },
      onError: () => haptic.error(),
    }),
  );

  return (
    <main className="ul-screen ul-screen--with-action">
      <h1 className="ul-screen__title">Рассылки</h1>

      {list.data ? (
        <ListGroup header="Аудитория">
          <ListRow icon="users" title="Все пользователи" value={list.data.audience.all} />
          <ListRow icon="language" title="Русский" value={list.data.audience.ru} />
          <ListRow icon="language" title="English" value={list.data.audience.en} />
        </ListGroup>
      ) : null}

      {list.isPending ? (
        <Skeleton height={240} />
      ) : list.isError ? (
        <div className="ul-error" role="alert">
          {errorText(list.error)}
        </div>
      ) : list.data.rows.length === 0 ? (
        <EmptyState
          icon="megaphone"
          title="Рассылок ещё не было"
          text="Первая появится здесь со статистикой доставки"
        />
      ) : (
        <div className="ul-multiline">
          <ListGroup header="История">
            {list.data.rows.map((b) => {
              const done = b.sent + b.blocked + b.failed;
              return (
                <ListRow
                  key={b.id}
                  icon="megaphone"
                  title={b.preview ?? 'Сообщение'}
                  subtitle={
                    <>
                      {`${b.startedAt ? dateTime(b.startedAt) : ''} · ${AUDIENCE[b.audience] ?? b.audience}`}
                      <span className="ul-stats">
                        <span title="Доставлено">
                          <Icon name="check" size={14} /> {b.sent}
                        </span>
                        <span title="Заблокировали бота">
                          <Icon name="blocked" size={14} /> {b.blocked}
                        </span>
                        <span title="Ошибки">
                          <Icon name="alert" size={14} /> {b.failed}
                        </span>
                        {b.status === 'sending' ? <span>{`${done} из ${b.total}`}</span> : null}
                      </span>
                    </>
                  }
                  trailing={<StatusBadge map={BROADCAST_STATUS} status={b.status} />}
                />
              );
            })}
          </ListGroup>
        </div>
      )}

      {start.isError ? (
        <div className="ul-error" role="alert">
          {errorText(start.error)}
        </div>
      ) : null}

      <div className="ul-group__footer">
        {inTelegram
          ? 'Кнопка ниже откроет чат с ботом: пришлите туда сообщение для рассылки.'
          : 'Бот пришлёт вам в Telegram приглашение составить рассылку. Или отправьте боту /broadcast.'}{' '}
        Премиум-эмодзи бот передаёт, если у владельца бота есть Telegram Premium.
      </div>

      <MainActionBar
        action={{ text: 'Новая рассылка', loading: start.isPending, onClick: () => start.mutate() }}
      />
    </main>
  );
}
